import { useEffect, useRef, type ReactNode } from 'react';
import { useSwipeReveal } from '@/hooks/use-swipe-reveal';
import { mobileLayer } from '@/lib/mobile-layer';
import { cn } from '@/lib/utils';

/**
 * Row that a horizontal drag to the left slides open, revealing `actions`
 * behind it on the right (PUSH-04 AC 1). Controlled by `open`; dragging back
 * or touching outside closes it. The state is observable in
 * `data-swipe-state` (L-009).
 */
export function SwipeableRow({
  open,
  onOpenChange,
  actions,
  actionsWidth = 160,
  children,
  className,
}: {
  open: boolean;
  onOpenChange(open: boolean): void;
  actions: ReactNode;
  actionsWidth?: number;
  children: ReactNode;
  className?: string;
}) {
  const rootRef = useRef<HTMLDivElement>(null);
  const { bind, offset, state } = useSwipeReveal({
    actionsWidth,
    open,
    onOpenChange,
  });

  // A drag ends with a click on whatever is under the finger (the card's
  // link): swallow that one click.
  const draggedRef = useRef(false);
  useEffect(() => {
    if (state === 'dragging') draggedRef.current = true;
  }, [state]);

  useEffect(() => {
    if (!open) return;
    const closeOnOutsideTouch = (event: PointerEvent) => {
      if (
        event.target instanceof Node &&
        rootRef.current?.contains(event.target)
      )
        return;
      onOpenChange(false);
    };
    document.addEventListener('pointerdown', closeOnOutsideTouch);
    return () =>
      document.removeEventListener('pointerdown', closeOnOutsideTouch);
  }, [open, onOpenChange]);

  const closed = state === 'closed';

  return (
    <div
      ref={rootRef}
      data-swipe-state={state}
      className={cn('relative overflow-hidden', mobileLayer.card, className)}
    >
      <div
        className="absolute inset-y-0 right-0 flex"
        style={{ width: actionsWidth }}
        aria-hidden={closed || undefined}
        inert={closed}
      >
        {actions}
      </div>
      <div
        {...bind}
        onPointerDown={(event) => {
          draggedRef.current = false;
          bind.onPointerDown(event);
        }}
        onClickCapture={(event) => {
          if (!draggedRef.current) return;
          draggedRef.current = false;
          event.preventDefault();
          event.stopPropagation();
        }}
        data-swipe-content
        className={cn(
          'relative bg-background',
          state !== 'dragging' && 'transition-transform duration-200 ease-out'
        )}
        style={{ touchAction: 'pan-y', transform: `translateX(${offset}px)` }}
      >
        {children}
      </div>
    </div>
  );
}
